import { compileTemplateComponentModule } from '../compile-template/compile-template';
import { markdownEditorComponentModule } from '../markdown-editor/markdown-editor';
import { codemirrorComponentModule } from '../codemirror/codemirror';
import { codemirrorOptionsComponentModule } from '../codemirror/codemirror-options';
import { regexHighlightComponentModule } from '../regex-highlight/regex-highlight';
import { highlightMatchesComponentModule } from '../highlight-matches/highlight-matches';
import { scrollToComponentModule } from '../scroll-to/scroll-to';
import { timerHelperModule } from '../../helpers/timer-helper';
import { globalEventsComponentModule } from '../global-events/global-events-directive';
import { keycodesModule } from '../../constants/keycodes';
import { fuzzyNumberFilterModule } from '../../filters/fuzzy-number';
import { horizontalCountdownTimerComponentModule } from '../horizontal-countdown-timer/horizontal-countdown-timer';
import { resizeToFillLeftoverComponentModule } from '../resize-to-fill-leftover/resize-to-fill-leftover';
import { voiceInputComponentModule } from '../voice-input/voice-input';
// templates
import quizChallengeQuestionsTemplate from './quiz-challenge-questions.tpl.html';
import quizChallengeQuestionMatcherTemplate from './quiz-challenge-question-matchers.tpl.html';
import previewMCTemplate from './preview/mc.tpl.html';
import previewTextTemplate from './preview/text.tpl.html';
import reviewMCTemplate from './review/mc.tpl.html';
import reviewTextTemplate from './review/text.tpl.html';
import solveCodeTemplate from './solve/code.tpl.html';
import solveLongTemplate from './solve/long.tpl.html';
import solvePlainTemplate from './solve/plain.tpl.html';
import solveMCMATemplate from './solve/mcma.tpl.html';
import solveMCSATemplate from './solve/mcsa.tpl.html';
import solveShortTemplate from './solve/short.tpl.html';

import './quiz-challenge-questions.less';
import { mappedMemo } from '../../utils/function-utils';
import { sortBy } from '../../utils/array-utils';
import { ngRepeatAliasDirectiveModule } from '../ng-repeat-alias/ng-repeat-alias';
import { defineValue } from '../../utils/object-utils/object-utils';

/**
 * This component is used to render questions in several ways:
 *
 * - As the details view
 * - As the "answers" view (which is the same thing)
 * - For taking the quiz
 * - For reviewing a candidate's submission
 *
 */

export const quizChallengeQuestionsComponentModule = angular
  .module('Q.components.quiz-challenge-questions', [
    compileTemplateComponentModule,
    markdownEditorComponentModule,
    codemirrorComponentModule,
    codemirrorOptionsComponentModule,
    regexHighlightComponentModule,
    highlightMatchesComponentModule,
    scrollToComponentModule,
    timerHelperModule,
    globalEventsComponentModule,
    keycodesModule,
    fuzzyNumberFilterModule,
    horizontalCountdownTimerComponentModule,
    resizeToFillLeftoverComponentModule,
    ngRepeatAliasDirectiveModule,
    voiceInputComponentModule,
  ])

  .component('quizChallengeQuestions', {
    bindings: {
      mode: '@',
      challenge: '<?',
      editButtons: '<',
      solution: '<',
      candidateReview: '<',
      summarize: '<',
      hideUnanswered: '<',
      disabled: '<',
      readonly: '<',
      highlightErrors: '<',
      onAnswerChange: '&',
      showIncompleteWarning: '<',
      afterQuizActionLabel: '<',
      onAfterQuizAction: '&',
      allowDarkMode: '<',
      useRandomizedQuestionOrder: '=?',
      solutionQuestionOrder: '<',
    },
    controller: 'QuizChallengeQuestionsCtrl',
    template: quizChallengeQuestionsTemplate,
  })

  .controller(
    'QuizChallengeQuestionsCtrl',
    function QuizChallengeQuestionsCtrl(
      $scope,
      $element,
      $timeout,
      QuizQuestion,
      quizQuestionMatcherNotes,
      timerHelper,
      KeyCodes,
      scrollToElement,
      $q,
      $log,
      quizTextQuestionCMOpts,
    ) {
      const ctrl = this;
      const el = $element[0];

      let usingChallengeSnapshot = false;

      function clearTimer() {
        startTimer(0, 0);
      }

      function startTimer(startedAt, timeoutSeconds) {
        ctrl.timerStartedAt = startedAt;
        ctrl.timerTimeoutSeconds = timeoutSeconds;
      }

      const TEMPLATES = {
        preview: {
          mc: previewMCTemplate,
          text: previewTextTemplate,
        },
        review: {
          mc: reviewMCTemplate,
          text: reviewTextTemplate,
        },
        solve: {
          // single line of code
          scode: solveCodeTemplate,
          // multiple lines of code
          lcode: solveCodeTemplate,
          // single line of unformatted text
          short: solveShortTemplate,
          // markdown editor
          long: solveLongTemplate,
          // plain text editor
          lplain: solvePlainTemplate,
          // markdown editor, full-page
          essay: solveLongTemplate,
          // plain text editor, full-page
          essayplain: solvePlainTemplate,
          // multiple choice, multiple answers (checkboxes)
          mcma: solveMCMATemplate,
          // multiple choice, single answer (radios)
          mcsa: solveMCSATemplate,
        },
      };

      function updateQuestionOrder() {
        const questionOrder =
          ctrl.solutionQuestionOrder ||
          (ctrl.solution && ctrl.solution.randomizedQuestionOrder);
        if (Array.isArray(questionOrder) && ctrl.useRandomizedQuestionOrder) {
          // to randomize the question order, we'll prototypically inherit the original model, then
          // override the questions array with the randomly sorted one
          ctrl._challenge = Object.create(ctrl._challenge);
          ctrl._challenge.questions = sortBy(
            ctrl._challenge.questions,
            (q1) => {
              return questionOrder.indexOf(q1.id) || Infinity;
            },
            'position',
          );
        }
      }

      function updateSolution() {
        if (ctrl.solution) {
          usingChallengeSnapshot = !!ctrl.solution.challengeSnapshot;
          if (usingChallengeSnapshot) {
            ctrl._challenge = ctrl.solution.getChallengeSnapshot();
          } else {
            ctrl._challenge = ctrl.challenge;
          }
          updateQuestionOrder();
          _refreshAnswers();
          if (ctrl.modes.solve) {
            ctrl.currentQuestionIdx = null;

            clearTimer();
            const challenge = ctrl._challenge;

            if (!challenge) {
              $log.error(
                "Invariant Violation: Expected challenge to be defined but it wasn't",
              );
            }

            if (challenge && challenge.isTimed()) {
              startTimer(
                ctrl.solution.timerStartedAt,
                challenge.timeLimitMin * 60,
              );
              if (ctrl.solution.canStillAnswer(challenge)) {
                ctrl.nextQuestion();
              } else {
                ctrl.currentQuestionIdx = challenge.questions.length;
              }
            } else {
              ctrl.nextQuestion();
            }
          }
          updateMatcherNotes();
        } else {
          ctrl._challenge = ctrl.challenge;
          updateQuestionOrder();
        }
      }

      ctrl.updateSolution = updateSolution;

      function _setupAnswer(question, answer) {
        answer.values = answer.values || [];
        if (question.isMultipleAnswer()) {
          defineValue(answer, '$mcmaValues', []);
          question.choices.forEach((_, cidx) => {
            answer.$mcmaValues[cidx] = answer.values.indexOf(cidx) !== -1;
          });
        }
      }

      function _setDefaultValue(question, answer) {
        if (question.isText() && question.defaultValue && !answer.values[0]) {
          answer.values[0] = question.defaultValue;
        }
      }

      function _refreshAnswers() {
        if (ctrl.solution) {
          // we need to make sure the answers are sorted and matching the challenge data,
          // and in the same order
          const answers = ctrl.solution.answers || [];

          ctrl.solution.answers = [];
          ((ctrl._challenge && ctrl._challenge.questions) || []).forEach(
            (q, idx) => {
              let answer = answers.find((a) => a.questionId === q.id);
              if (!answer) {
                answer = {
                  questionId: q.id,
                  values: [],
                };
              }
              ctrl.solution.answers[idx] = answer;
              _setupAnswer(q, answer);
              if (!ctrl._challenge.isQuestionTimed()) {
                _setDefaultValue(q, answer);
              }
            },
          );

          _checkAllAnswered();
        }
      }

      ctrl.currentQuestionIdx = null;
      ctrl.showQuestion = ($index) => {
        if (ctrl.modes.solve) {
          if (!ctrl.solution) {
            return false;
          }
          if (ctrl._challenge.isQuestionTimed()) {
            return ctrl.currentQuestionIdx === $index;
          }
        } else if (ctrl.modes.review && ctrl.hideUnanswered) {
          return ctrl.solution && ctrl.solution.answered($index);
        }
        return true;
      };

      function _checkAllAnswered() {
        ctrl.allAnswered =
          ctrl.solution &&
          typeof ctrl.solution.allAnswered === 'function' &&
          ctrl.solution.allAnswered();
        return ctrl.allAnswered;
      }

      ctrl._onAnswerChange = ($questionIndex) => {
        // check if every question has an answer
        _checkAllAnswered();
        const answer = ctrl.solution && ctrl.solution.answers[$questionIndex];
        const question = ctrl._challenge.questions[$questionIndex];

        if (answer && !answer.completedAt) {
          // track when the answer was last updated
          answer.answeredAt = new Date();

          // Calculate and update length score if length validation is enabled
          if (
            question.options &&
            question.options.lengthValidation &&
            question.options.lengthValidation.enabled
          ) {
            // Calculate length score from the current answer text
            const answerText = answer.values[0] || '';
            answer.lengthScore = question.calculateLengthScore(answerText);

            // Also update the total score based on accuracy and length scores
            if (answer.accuracyScore !== undefined) {
              answer.score = (answer.accuracyScore + answer.lengthScore) / 2;
            }
          }
        }

        $q.when(ctrl.onAnswerChange()).then(() => {
          if (!ctrl.allAnswered) {
            // Also check if every question has an answer after saving, but only to mark it if it already
            // isn't. This is because the async events can come out of order, and we don't want to clear
            // the state accidentally
            _checkAllAnswered();
          }
        });
      };

      function _setupSolver() {
        ctrl.nextQuestion = (reverse) => {
          const challenge = ctrl._challenge;
          if (
            reverse !== true &&
            ctrl.currentQuestionIdx >= challenge.questions.length
          ) {
            ctrl.onAfterQuizAction();
          } else if (ctrl.solution) {
            let scrollToQuestionIdx = 0;
            if (challenge.isQuestionTimed()) {
              // Note: reverse is ignored for question-timed, you can only go forwards
              const answers = ctrl.solution.answers;
              if (answers[ctrl.currentQuestionIdx]) {
                // mark previous question as answered
                answers[ctrl.currentQuestionIdx].completedAt = new Date();
              }
              // find the first question that wasn't answered yet _and_ has time left
              ctrl.currentQuestionIdx = ctrl.solution.needsAnswerIdx(
                challenge,
                answers,
              );
              if (ctrl.currentQuestionIdx === -1) {
                scrollToQuestionIdx = ctrl.currentQuestionIdx =
                  challenge.questions.length;
                clearTimer();
                ctrl.completed = true;
              } else {
                const q = challenge.questions[ctrl.currentQuestionIdx];
                const ans = answers[ctrl.currentQuestionIdx];
                if (!ans.startedAt) ans.startedAt = new Date();
                _setDefaultValue(q, ans);
                startTimer(ans.startedAt, q._timeLimitSec(challenge));
              }

              ctrl._onAnswerChange();
            } else {
              // switch focus forwards or backwards for other types of quizzes
              let qIdx = ctrl.currentQuestionIdx;
              if (ctrl.currentQuestionIdx === null) {
                qIdx = 0;
              } else if (reverse === true && qIdx > 0) {
                qIdx--;
              } else if (
                reverse !== true &&
                qIdx < challenge.questions.length
              ) {
                qIdx++;
              }
              scrollToQuestionIdx = ctrl.currentQuestionIdx = qIdx;
            }

            $timeout(() => {
              if (scrollToQuestionIdx >= challenge.questions.length) {
                // focus on the "next challenge" button
                const nextButton = $element.querySelector(
                  '.after-quiz-action button',
                );
                if (nextButton.length) {
                  nextButton.focus();
                  scrollToElement({ target: nextButton });
                }
              } else {
                const question =
                  el.querySelectorAll('.question')[scrollToQuestionIdx];
                if (!question) return;
                const inputElement = question.querySelector(
                  '.answer input, .answer textarea',
                );
                const questionsContent = el.querySelector('.questions-content');
                const scroll = questionsContent && questionsContent.scrollTop;
                if (inputElement && inputElement.focus) {
                  inputElement.focus();
                }
                if (scrollToQuestionIdx === 0 && reverse !== true) {
                  // always scroll to top
                  if (questionsContent) {
                    questionsContent.scrollTop = 0;
                  }
                } else {
                  questionsContent.scrollTop = scroll;
                  const promptElement = question.querySelector('.prompt');
                  if (promptElement) {
                    $timeout(() => {
                      scrollToElement({ target: promptElement, center: 0 });
                    }, 250);
                  }
                }
              }
            }, 100);
          }
        };

        ctrl.focusOn = (index) => {
          // track manual focus changes (i.e.: user clicking through or tabbing through)
          ctrl.currentQuestionIdx = index;
        };

        const KEY_HINTS = `ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789,./;'[]-=\\`;
        const KEY_HINT_CHARS = KEY_HINTS.toLowerCase();

        ctrl.keyHintFor = ($index) => {
          return KEY_HINTS[$index] || '';
        };

        ctrl.nextQuestionKeyStroke = `${KeyCodes.SYMBOLS.COMMAND_KEY} + ${KeyCodes.SYMBOLS.ENTER}`;
        // allow global keystrokes for "next" and selecting multiple choice answers via keystrokes
        $scope.$on('keydown', ($event, e, props) => {
          // only listen if we have a solution, and on descendants or parents of this element
          if (
            !ctrl.solution ||
            !e.target ||
            !(el.contains(e.target) || e.target.contains(el))
          ) {
            return;
          }

          const reverse = KeyCodes.isShift(e);

          if (
            props.common === 'enter' &&
            (!ctrl.nextDisabled() || reverse) &&
            KeyCodes.isCommand(e)
          ) {
            $event.preventDefault();
            $scope.$applyAsync(() => {
              ctrl.nextQuestion(reverse);
            });
          } else {
            const keyhintIndex = KEY_HINT_CHARS.indexOf(props.char);
            if (keyhintIndex > -1) {
              const $questionIndex = ctrl.currentQuestionIdx;
              const q = ctrl._challenge.questions[$questionIndex];
              const answer =
                ctrl.solution.answers && ctrl.solution.answers[$questionIndex];
              if (
                q &&
                q.isMultipleChoice() &&
                keyhintIndex < q.choices.length &&
                answer
              ) {
                $event.preventDefault();
                $scope.$applyAsync(() => {
                  if (q.maxSelections === 1) {
                    answer.values[0] = keyhintIndex;
                    ctrl._onAnswerChange($questionIndex);
                  } else {
                    answer.$mcmaValues[keyhintIndex] =
                      !answer.$mcmaValues[keyhintIndex];
                    ctrl.updateMCMA(q, $questionIndex, keyhintIndex);
                  }
                });
              }
            }
          }
        });
        ctrl.nextQuestionLabel = () => {
          return ctrl.completed && ctrl.afterQuizActionLabel
            ? ctrl.afterQuizActionLabel
            : 'Next Question';
        };
        ctrl.nextDisabled = () => {
          const index = ctrl.currentQuestionIdx;
          const allAnswered =
            ctrl.completed ||
            (!ctrl.challenge.isQuestionTimed() && ctrl.allAnswered);

          const { questions } = ctrl.challenge;

          if (allAnswered && questions && index >= questions.length - 1) {
            return !ctrl.afterQuizActionLabel;
          }
          return (
            !ctrl.solution ||
            (!ctrl.expired && index !== null && !ctrl.solution.answered(index))
          );
        };
        ctrl.questionsProgress = () => {
          const questions = ctrl._challenge.questions;
          if (!questions || !questions.length) {
            return '';
          }
          const question = ctrl.completed
            ? questions.length
            : ctrl.currentQuestionIdx + 1;
          return `${question} / ${questions.length} Questions`;
        };
      }

      ctrl.$onInit = () => {
        ctrl.mode = ctrl.mode || 'preview';
        ctrl.modes = {
          [ctrl.mode]: true,
        };
        if (!ctrl.challenge && ctrl.solution) {
          ctrl.challenge = ctrl.solution.challenge;
        }
        ctrl._challenge = ctrl.challenge;
        if (ctrl.modes.solve) {
          _setupSolver();
        }

        if (ctrl.useRandomizedQuestionOrder === undefined) {
          ctrl.useRandomizedQuestionOrder = !!(
            ctrl.modes.solve || ctrl.candidateReview
          );
        }
        updateSolution();

        $scope.$on('socket:model.changed:Solution', (event, data) => {
          if (data.id && ctrl.solution && data.id === ctrl.solution.id) {
            _refreshAnswers();
          }
        });

        // configure change detection AFTER initialization
        ctrl.$onChanges = (changes) => {
          if (changes.solution || changes.challenge) {
            updateSolution();
          }
        };
      };

      ctrl.quizClasses = () => {
        let c = [ctrl.mode];
        if (ctrl.summarize) c.push('summarize');
        if (ctrl.highlightErrors === false)
          c.push('disable-error-highlighting');
        if (ctrl.candidateReview) c.push('candidate-review');
        // blockInstructionCopy defaults true (see QuizChallenge); only absent when authored off
        if (ctrl._challenge && ctrl._challenge.blockInstructionCopy !== false)
          c.push('copy-protected');
        c.push(
          ctrl._challenge && ctrl._challenge.isQuestionTimed()
            ? 'question-timed'
            : 'not-question-timed',
        );
        return c.join(' ');
      };

      let watchingSolution = false;
      ctrl.matcherNotes = [];

      function updateMatcherNotes() {
        ctrl.matcherNotes = [];
        if (
          ctrl.solution &&
          ctrl.modes.review &&
          ctrl.solution.answers &&
          ctrl._challenge.questions
        ) {
          ctrl._challenge.questions.forEach((q, idx) => {
            const answer = ctrl.solution.answers[idx];
            const aiResponse = answer && answer.aiResponse;
            const answerValue = answer && answer.values && answer.values[0];
            const matcherAnswer = aiResponse || answerValue;
            ctrl.matcherNotes[idx] = quizQuestionMatcherNotes.generate(
              matcherAnswer,
              q,
            );
          });

          if (!watchingSolution) {
            watchingSolution = true;
            $scope.$watch(
              () =>
                (ctrl.solution.answers || [])
                  .map((a) => a.values.join('|'))
                  .join(';'),
              updateMatcherNotes,
            );
          }
        }
      }

      ctrl.showValidationDetails = (question, index) => {
        const answers = ctrl.solution && ctrl.solution.answers;
        const answer = answers && answers[index];
        const mNotes = ctrl.matcherNotes[index];
        return (
          ctrl.modes.review &&
          !ctrl.candidateReview &&
          question.isText() &&
          answer &&
          answer.values &&
          answer.values[0] &&
          mNotes &&
          mNotes.results.length
        );
      };
      ctrl.showOptionsSummary = (question, index) => {
        return (
          !ctrl.modes.solve &&
          !ctrl.candidateReview &&
          !ctrl.showValidationDetails(question, index)
        );
      };

      ctrl.questionExponent = function () {
        let count =
          ctrl._challenge &&
          ctrl._challenge.questions &&
          ctrl._challenge.questions.length;
        // we support properly rendering up to 999 questions :-)
        return count < 10 ? 1 : count < 100 ? 2 : 3;
      };

      ctrl.questionTemplate = (question) => {
        const modeTemplates = TEMPLATES[ctrl.mode];
        let template;
        if (ctrl.modes.preview || ctrl.modes.review) {
          template = modeTemplates[question.isText() ? 'text' : 'mc'];
        } else {
          template = modeTemplates[question.answerType()];
        }
        return template;
      };

      ctrl.errorClass = (questionIndex) => {
        if (ctrl.highlightErrors && ctrl.modes.solve && ctrl.solution) {
          let empty = true;
          let answers = ctrl.solution.answers;
          if (
            answers &&
            answers[questionIndex] &&
            answers[questionIndex].values
          ) {
            let val = answers[questionIndex].values[0];
            empty = !(val || val === 0);
          }
          if (empty) return 'has-error';
        }
        if (ctrl.modes.review) {
          let classes = 'unanswered';
          let answers = ctrl.solution && ctrl.solution.answers;
          if (answers && answers[questionIndex]) {
            if (answers[questionIndex].score < 100) {
              classes = answers[questionIndex].values.length
                ? 'has-error'
                : 'has-error unanswered';
            } else {
              classes = '';
            }
          }
          return classes;
        }
        return '';
      };

      ctrl.updateMCMA = (question, $questionIndex, $choiceIndex) => {
        if (ctrl.solution && ctrl.solution.answers) {
          let answer = ctrl.solution.answers[$questionIndex];
          if (question.isMultipleAnswer()) {
            if (!Array.isArray(answer.$mcmaValues)) {
              _setupAnswer(question, answer);
            }
            // if the max number of selections have already been reached then prevent additional selections
            if (
              answer.$mcmaValues[$choiceIndex] &&
              question.updateMaxSelections() === answer.values.length
            ) {
              answer.$mcmaValues[$choiceIndex] = false;
            } else {
              answer.values = [];
              answer.$mcmaValues.forEach((val, idx) => {
                if (val) {
                  answer.values.push(idx);
                }
              });
            }
          }
        }
        ctrl._onAnswerChange($questionIndex);
      };

      ctrl.onKeyDown = function ($questionIndex) {
        const answer = ctrl.solution.answers[$questionIndex];
        if (!answer.typingStartedAt) {
          answer.typingStartedAt = new Date();
        }
        // Increase the typed characters count
        if (!answer.typedKeysCount) {
          answer.typedKeysCount = 0;
        }
        // Only count printable characters, not special keys like shift, ctrl, etc
        answer.typedKeysCount++;
      };
      // Register typing end
      ctrl.onBlur = function ($questionIndex) {
        const answer = ctrl.solution.answers[$questionIndex];
        answer.lastTypedAt = new Date();
      };

      quizTextQuestionCMOpts.create(ctrl);
    },
  )

  .factory(
    'quizTextQuestionCMOpts',
    function quizTextQuestionCMOpts(codemirrorOptions) {
      const filenameFromQuestion = (question) =>
        question.answerType() + '-quiz-question.' + question.editorLanguage();
      return {
        create(ctrl) {
          ctrl.codemirrorOptions = codemirrorOptions;
          let optsCache = {};
          ctrl.createCMOpts = mappedMemo(
            filenameFromQuestion,
            (filename, question) => {
              if (!optsCache[filename]) {
                const opts = codemirrorOptions.createOpts(filename);
                optsCache[filename] = opts;
                if (question.answerType() === 'scode') {
                  angular.extend(opts, {
                    lineNumbers: false,
                    foldGutter: false,
                    gutters: [],
                    styleActiveLine: false,
                    onLoad(cm) {
                      cm.on('beforeChange', function (cm, changeObj) {
                        if (changeObj.text.length > 1) {
                          changeObj.text = [changeObj.text.join('')];
                        }
                      });
                    },
                  });
                  opts.extraKeys.Tab = false;
                  opts.extraKeys['Shift-Tab'] = false;
                }
              }
              return optsCache[filename];
            },
          );
        },
      };
    },
  )

  .factory('quizQuestionMatcherNotes', function () {
    return {
      // Method to generate points based on matchers selected and answer given
      generate(answer, question) {
        const data = {
          highlightMatches: [],
          results: [],
          points: answer ? question.weight : 0,
        };
        const matchers = question.matchers || [];
        // Note: we're checking to make sure this is a string because sometimes it isn't
        // This shouldn't happen, but I think it's due to a disconnect between old DB values that aren't
        // syncing up at some point (say, a challenge was changed later and something isn't matching)
        // It's hard to find, so this is a simple check to at least prevent errors.
        if (matchers.length && angular.isString(answer)) {
          let correctMatchers = 0;
          matchers.forEach((m) => {
            let match = m.value;
            let matched;
            if (m.type === 'exact') {
              if (m.case) {
                matched = answer === match;
              } else {
                matched = answer.toLowerCase() === match.toLowerCase();
              }
            } else if (m.type === 'includes') {
              if (m.case) {
                matched = answer.indexOf(match) > -1;
              } else {
                matched =
                  answer.toLowerCase().indexOf(match.toLowerCase()) > -1;
              }
            } else {
              match = new RegExp(
                match,
                (m.case ? '' : 'i') + (m.multi ? 'm' : ''),
              );
              matched = match.test(answer);
            }
            const correct = m.negate ? !matched : matched;
            if (correct) correctMatchers++;
            const mData = {
              match,
              matched,
              correct,
              keyword: match,
              className: correct ? 'correct' : 'incorrect',
              caseSensitive: !!m.case,
            };
            data.results.push(mData);
            if (m.type !== 'exact') {
              data.highlightMatches.push(mData);
            }
          });
          if (question.allOrNothing) {
            data.points =
              correctMatchers === matchers.length ? question.weight : 0;
          } else {
            data.points =
              Math.round(
                (100 * question.weight * correctMatchers) / matchers.length,
              ) / 100;
          }
        }
        return data;
      },
    };
  })

  .component('quizChallengeQuestionMatchers', {
    bindings: {
      question: '<',
      results: '<',
    },
    template: quizChallengeQuestionMatcherTemplate,
    controller: /* @ngInject */ function (QuizQuestion) {
      const ctrl = this;

      ctrl.getMatcherType = (item, idx) => {
        // returns a shortened form
        let result = ctrl.results && ctrl.results[idx];
        return QuizQuestion.getMatcherLabel(item, result);
      };

      ctrl.matcherClass = (m, idx) => {
        let matcherClass = m.type;
        let result = ctrl.results && ctrl.results[idx];
        if (result) {
          matcherClass += ' ' + (result.correct ? 'correct' : 'incorrect');
        }
        return matcherClass;
      };
    },
  });
